<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from "vue";
import type ymaps from "yandex-maps";
import { useAnalyticManager } from "@magnit/analytic-events";
import type {
  IShopSelectMapProps,
  IMapBoundingBox,
  IDisplayShop,
  IShopSelectShop,
} from "../types";
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_GRID_SIZE,
  MAP_DEFAULT_MAP_OBJECTS_OPTIONS,
  MAP_DEFAULT_MAP_GEOLOCATION_OBJECT_OPTIONS,
  MAP_DEFAULT_MAP_CLUSTER_OPTIONS,
  MAP_DEFAULT_BALLOON_OPTIONS,
  MAP_DEFAULT_MARKER_OPTIONS,
  MAP_RENDER_TICK,
  MAP_GEOLOCATION_PIN_ID,
  LABEL_SELECT_SHOP_ACTION,
} from "../constants";
import {
  asyncCondition,
  fetchGeolocation,
  convertShopToMarker,
  convertShopToTargetMarker,
  getGeolocationMarker,
} from "../utils";
import { ShopSelectMapControls, ShopSelectToolbarButtons } from "../enums";
import VLoader from "@magnit/core/src/components/VLoader/VLoader.vue";
import ShopSelectError from "./ShopSelectError.vue";
import ShopSelectToolbar from "./ShopSelectToolbar.vue";

const props = withDefaults(defineProps<IShopSelectMapProps>(), {
  displayMap: false,
  loading: true,
  loadingError: false,
  loadingErrorMessage: "",
  mapElementId: "",
  center: () => [...DEFAULT_MAP_CENTER],
  gridSize: DEFAULT_MAP_GRID_SIZE,
  mapObjectOptions: () => ({ ...MAP_DEFAULT_MAP_OBJECTS_OPTIONS }),
  geolocationObjectOptions: () => ({
    ...MAP_DEFAULT_MAP_GEOLOCATION_OBJECT_OPTIONS,
  }),
  clusterOptions: () => ({ ...MAP_DEFAULT_MAP_CLUSTER_OPTIONS }),
  balloonOptions: () => ({ ...MAP_DEFAULT_BALLOON_OPTIONS }),
  markerOptions: () => ({ ...MAP_DEFAULT_MARKER_OPTIONS }),
  selectActionLabel: LABEL_SELECT_SHOP_ACTION,
  shops: () => [],
  clusterize: true,
  selectedShop: null,
  targetShop: null,
  autocenterKey: 0,
});
const emit = defineEmits<{
  "initialize:map": [payload: { map: ymaps.Map }];
  "error:marker": [err: unknown];
  "error:map": [err: unknown];
  "error:geolocation": [err: unknown];
  "drag:map": [];
  "click:balloon-select": [code: string];
  "open:balloon": [code: string];
  "close:balloon": [];
}>();

const { send } = useAnalyticManager();
let ymap: ymaps.Map | null = null;
let shopsObjectManager: ymaps.ObjectManager | null = null;
let targetShopObjectManager: ymaps.ObjectManager | null = null;
let selectedShopObjectManager: ymaps.ObjectManager | null = null;
let geolocationObjectManager: ymaps.ObjectManager | null = null;

const zoom = ref<number | null>(null);
const mapEl = ref<HTMLElement | null>(null);
const geolocationDisabledRef = ref(false);

const boundingBox = defineModel<IMapBoundingBox | null>("boundingBox", {
  default: null,
});

const displayMapElement = computed(
  () => !!(props.displayMap && !props.loadingError && props.mapElementId),
);
const minZoom = computed(() => props.customOptions?.zoomRange?.[0] ?? 2);
const maxZoom = computed(() => props.customOptions?.zoomRange?.[1] ?? 15);
const zoomControlEnabled = computed(() =>
  (props.customOptions?.controls ?? []).includes(ShopSelectMapControls.ZOOM),
);
const geolocationEnabled = computed(() =>
  (props.customOptions?.controls ?? []).includes(
    ShopSelectMapControls.GEOLOCATION,
  ),
);
const toolbarButtons = computed(() => {
  const zoomInDisabled = zoom.value === null || maxZoom.value <= zoom.value;
  const zoomOutDisabled = zoom.value === null || minZoom.value >= zoom.value;
  const buttonsArr = [
    {
      button: {
        type: ShopSelectToolbarButtons.ZOOM_IN,
        disabled: zoomInDisabled,
      },
      enabled: zoomControlEnabled.value,
    },
    {
      button: {
        type: ShopSelectToolbarButtons.ZOOM_OUT,
        disabled: zoomOutDisabled,
      },
      enabled: zoomControlEnabled.value,
    },
    {
      button: {
        type: ShopSelectToolbarButtons.GEOLOCATION,
        disabled: geolocationDisabledRef.value,
      },
      enabled: geolocationEnabled.value,
    },
  ];
  return buttonsArr.filter((s) => s.enabled === true).map((s) => s.button);
});
const clusteringZoomLimit = computed(() => {
  const prop = props.customOptions?.clusteringZoomLimit;
  return prop !== undefined && Number.isFinite(prop) ? prop : maxZoom.value;
});
const clusterMode = computed(() => {
  if (!props.clusterize || zoom.value === null) return true;
  return zoom.value < clusteringZoomLimit.value;
});
const clusterShops = computed(() => {
  if (!props.selectedShop?.code) return props.shops;
  const code = props.selectedShop.code;
  return props.shops.filter((s) => s.code !== code);
});

const getMapElement = () => document.getElementById(props.mapElementId);
const setupObjectManager = () => {
  try {
    const win = window as any;
    shopsObjectManager = new win.ymaps.ObjectManager({
      clusterize: props.clusterize,
      gridSize: props.gridSize,
      clusterOpenBalloonOnClick: false,
      viewportMargin: 0,
    });
    if (shopsObjectManager === null) return;
    shopsObjectManager.objects.options.set({
      ...props.mapObjectOptions,
      iconContentLayout: win.ymaps.templateLayoutFactory.createClass(
        props.mapObjectOptions.iconContentLayout,
      ),
    });
    if (props.clusterize) {
      shopsObjectManager.clusters.options.set({
        clusterIconLayout: win.ymaps.templateLayoutFactory.createClass(
          props.clusterOptions.clusterIconLayout,
        ),
        clusterIconShape: {
          type: props.clusterOptions.type,
          coordinates: props.clusterOptions.coordinates,
          radius: props.clusterOptions.radius,
        },
      });
    }
  } catch (err) {
    emit("error:map", err);
  }
};
const setupTargetShopObjectManager = () => {
  try {
    const win = window as any;
    targetShopObjectManager = new win.ymaps.ObjectManager({
      clusterize: false,
    });
    if (targetShopObjectManager === null) return;
    targetShopObjectManager.objects.options.set({
      ...props.mapObjectOptions,
      iconContentLayout: win.ymaps.templateLayoutFactory.createClass(
        props.mapObjectOptions.iconContentLayout,
      ),
    });
  } catch (err) {
    emit("error:map", err);
  }
};
const setupSelectedShopObjectManager = () => {
  try {
    const win = window as any;
    selectedShopObjectManager = new win.ymaps.ObjectManager({
      clusterize: false,
    });
    if (selectedShopObjectManager === null) return;
    selectedShopObjectManager.objects.options.set({
      ...props.mapObjectOptions,
      iconContentLayout: win.ymaps.templateLayoutFactory.createClass(
        props.mapObjectOptions.iconContentLayout,
      ),
    });
  } catch (err) {
    emit("error:map", err);
  }
};
const setupGeolocationObjectManager = () => {
  try {
    const win = window as any;
    geolocationObjectManager = new win.ymaps.ObjectManager({
      clusterize: false,
    });
    if (geolocationObjectManager === null) return;
    geolocationObjectManager.objects.options.set({
      ...props.geolocationObjectOptions,
      iconContentLayout: win.ymaps.templateLayoutFactory.createClass(
        props.geolocationObjectOptions.iconContentLayout,
      ),
    });
  } catch (err) {
    emit("error:map", err);
  }
};
const setupBalloonLayout = () => {
  try {
    const win = window as any;
    const balloonLayout: ymaps.ILayout =
      win.ymaps.templateLayoutFactory.createClass(
        props.balloonOptions.layoutHtml,
        {
          build() {
            this.constructor.superclass.build.call(this);
            this.element = this.getParentElement().querySelector(
              props.balloonOptions.balloonSelector,
            );
            this.applyElementOffset();
            this.element
              .querySelector(props.balloonOptions.closeButtonSelector)
              .addEventListener("click", (e: Event) => {
                e.preventDefault();
                e.stopPropagation();
                emit("close:balloon");
              });
            const buttonEl = this.element.querySelector(
              props.balloonOptions.submitButtonSelector,
            );
            buttonEl?.addEventListener("click", () => {
              emit("click:balloon-select", props.targetShop!.code as string);
            });
          },
          clear() {
            this.constructor.superclass.clear.call(this);
            if (props.targetShop) emit("close:balloon");
          },
          applyElementOffset() {
            Object.assign(this.element.style, {
              left: `${-(this.element.offsetWidth / 2)}px`,
              top: `${-this.element.offsetHeight}px`,
            });
          },
          onSublayoutSizeChange() {
            this.applyElementOffset();
            this.events.fire("shapechange");
          },
          getShape() {
            const style = getComputedStyle(this.element);
            const position = {
              left: parseFloat(style.left),
              top: parseFloat(style.top),
            };
            return new win.ymaps.shape.Rectangle(
              new win.ymaps.geometry.pixel.Rectangle([
                [position.left, position.top],
                [
                  position.left + this.element.offsetWidth,
                  position.top + this.element.offsetHeight,
                ],
              ]),
            );
          },
        },
      );
    const balloonContentLayout = win.ymaps.templateLayoutFactory.createClass(
      props.balloonOptions.balloonContentLayout,
    );
    targetShopObjectManager?.objects.options.set({
      balloonLayout,
      balloonContentLayout,
    });
  } catch (err) {
    emit("error:map", err);
  }
};
const refreshShopMarkers = () => {
  if (shopsObjectManager === null) return;
  const markers = clusterShops.value
    .map((marker) => {
      return convertShopToMarker(marker, props.markerOptions);
    })
    .filter(Boolean);
  shopsObjectManager.removeAll();
  if (!markers.length) return;
  shopsObjectManager.add(markers);
};
const addSelecltedShopMarker = () => {
  if (!props.selectedShop) return;
  const selectedShopMarker = convertShopToMarker(
    props.selectedShop,
    props.markerOptions,
    { xl: true },
  );
  selectedShopObjectManager!.add([selectedShopMarker]);
};
const handleShopsChange = (next: IDisplayShop[], prev: IDisplayShop[]) => {
  if (shopsObjectManager === null) return;
  const nextCodes: string[] = [];
  for (let i = 0, len = next.length; i < len; i += 1) {
    const code = next[i].code;
    if (code && code !== props.targetShop?.code) nextCodes.push(code);
  }
  const prevCodes: string[] = [];
  const markersToAdd: ReturnType<typeof convertShopToMarker>[] = [];
  const markersToRemove: object[] = [];
  for (let i = 0, len = prev.length; i < len; i++) {
    const single = prev[i];
    prevCodes.push(single.code);
    if (
      !nextCodes.includes(single.code) &&
      props.selectedShop?.code !== single.code
    ) {
      const marker = shopsObjectManager.objects.getById(single.code);
      if (marker) markersToRemove.push(marker);
    }
  }
  for (let i = 0, len = next.length; i < len; i++) {
    const single = next[i];
    if (!prevCodes.includes(single.code)) {
      const marker = convertShopToMarker(single, props.markerOptions);
      if (marker) markersToAdd.push(marker);
    }
  }
  if (markersToRemove.length) shopsObjectManager.remove(markersToRemove);
  if (markersToAdd.length) shopsObjectManager.add(markersToAdd);
};
const handleBoundingBoxUpdate = () => {
  if (ymap === null) return null;
  const [bottomLeft, topRight] = ymap.getBounds();
  boundingBox.value = {
    leftTopLatitude: topRight[0],
    leftTopLongitude: bottomLeft[1],
    rightBottomLatitude: bottomLeft[0],
    rightBottomLongitude: topRight[1],
  };
};
const initMap = async () => {
  try {
    if (ymap !== null) return;
    await asyncCondition(() => !!getMapElement());
    const win = window as any;
    const initOptions = {
      zoom: props.zoom,
      center: props.center,
      controls: [],
    };
    const options = {
      minZoom: minZoom.value,
      maxZoom: maxZoom.value,
      maxAnimationZoomDifference: Infinity,
    };
    ymap = new win.ymaps.Map(getMapElement(), initOptions, options);
    if (!ymap) throw new Error("initMap: map is null");
    zoom.value = initOptions.zoom ?? 5;
    setupObjectManager();
    setupTargetShopObjectManager();
    setupSelectedShopObjectManager();
    setupGeolocationObjectManager();
    setupBalloonLayout();
    if (
      !shopsObjectManager ||
      !targetShopObjectManager ||
      !selectedShopObjectManager ||
      !geolocationObjectManager
    )
      throw "Ошибка инициалищзации менеджеров карты";
    ymap.geoObjects.add(shopsObjectManager);
    ymap.geoObjects.add(targetShopObjectManager);
    ymap.geoObjects.add(selectedShopObjectManager);
    ymap.geoObjects.add(geolocationObjectManager);
    setInitialEvents();
    refreshShopMarkers();
    addSelecltedShopMarker();
    autoCenterMap();
    emit("initialize:map", { map: ymap });
    if (props.targetShop) {
      openMarkerBalloon(props.targetShop);
    } else {
      handleBoundingBoxUpdate();
    }
  } catch (err) {
    console.error({ err });
    emit("error:map", err);
  }
};
const setInitialEvents = () => {
  if (ymap === null) return;
  ymap.events.add("boundschange", (ev) => {
    const [newZoom, oldZoom] = [ev.get("newZoom"), ev.get("oldZoom")];
    if (newZoom !== undefined && newZoom !== oldZoom) zoom.value = newZoom;
    handleBoundingBoxUpdate();
  });
  ymap.events.add("mousedown", () => emit("drag:map"));
  ymap.events.add("multitouchstart", () => emit("drag:map"));
  shopsObjectManager!.events.add("click", (ev) => {
    const code = ev.get("objectId");
    const isCluster = Number.isNaN(parseInt(code as string, 10));
    if (code && !isCluster) emit("open:balloon", code);
  });
  selectedShopObjectManager!.events.add("click", () => {
    emit("open:balloon", props.selectedShop!.code);
  });
};
const handleZoomIn = () => {
  const currentZoom = ymap?.getZoom();
  if (currentZoom === undefined || currentZoom >= maxZoom.value) return;
  ymap?.setZoom(currentZoom + 1);
  zoom.value = currentZoom + 1;
};
const handleZoomOut = () => {
  const currentZoom = ymap?.getZoom();
  if (currentZoom === undefined || currentZoom <= minZoom.value) return;
  ymap?.setZoom(currentZoom - 1);
};
const handleGeolocation = async () => {
  if (geolocationDisabledRef.value || !geolocationObjectManager) return;
  send("ShopSelect:MapStore:Geo:Click");
  const geolocation = geolocationObjectManager.objects.getById(
    MAP_GEOLOCATION_PIN_ID,
  ) as ReturnType<typeof getGeolocationMarker> | null;
  if (geolocation?.geometry?.coordinates)
    return ymap!.setCenter(
      geolocation.geometry.coordinates,
      props.customOptions?.centerZoom,
    );
  try {
    geolocationDisabledRef.value = true;
    const position = await fetchGeolocation();
    if (position === null) throw "Ошибка определения геолокации";
    const marker = getGeolocationMarker(position);
    geolocationObjectManager.add([marker]);
    ymap!.setCenter(position, props.customOptions?.centerZoom);
  } catch (err) {
    emit("error:geolocation", err);
  } finally {
    geolocationDisabledRef.value = false;
  }
};
const autoCenterMap = () => {
  const bounds = shopsObjectManager?.getBounds() ?? null;
  if (!ymap || bounds === null) return;
  ymap.setBounds(bounds, {
    checkZoomRange: true,
    zoomMargin: [40, 40],
  });
};
const handleToolbarButtonClick = (type: ShopSelectToolbarButtons) => {
  if (type === ShopSelectToolbarButtons.ZOOM_IN) return handleZoomIn();
  if (type === ShopSelectToolbarButtons.ZOOM_OUT) return handleZoomOut();
  if (type === ShopSelectToolbarButtons.GEOLOCATION) return handleGeolocation();
};
const openMarkerBalloon = (shop: IShopSelectShop) => {
  if (!ymap || !shop) return;
  const marker = shopsObjectManager!.objects.getById(shop.code);
  if (marker) shopsObjectManager!.remove(marker);
  const selected = props.selectedShop && props.selectedShop.code === shop.code;
  if (selected) selectedShopObjectManager!.removeAll();
  targetShopObjectManager!.removeAll();
  const newMarker = convertShopToTargetMarker(shop, props.markerOptions, {
    xl: selected,
    displayAction: !selected,
  });
  targetShopObjectManager!.add(newMarker ? [newMarker] : []);
  ymap!.setCenter(
    [shop.latitude, shop.longitude],
    props.customOptions?.centerZoom,
  );
  setTimeout(() => {
    const controller = targetShopObjectManager?.objects as any;
    controller.balloon.open(shop.code);
  }, MAP_RENDER_TICK);
};
const closeMarkerBalloon = (shop: IShopSelectShop) => {
  if (!shop.code || !ymap) return;
  const marker = targetShopObjectManager!.objects.getById(shop.code);
  const selected = props.selectedShop && props.selectedShop.code === shop.code;
  if (marker && selected) addSelecltedShopMarker();
  else if (marker) shopsObjectManager!.add(marker);
  targetShopObjectManager!.removeAll();
};

onMounted(() => {
  if (!displayMapElement.value) return;
  initMap();
  send("ShopSelect:MapStore:View");
});
watch(displayMapElement, (next) => {
  if (next) initMap();
});
watch(clusterShops, (next, prev) => handleShopsChange(next, prev));
watch(
  () => props.targetShop,
  (next, prev) => {
    if (next === null && prev?.code) closeMarkerBalloon(prev);
    else if (next !== null) openMarkerBalloon(next);
  },
);
watch(
  () => props.center,
  (next) => {
    if (next?.length !== 2 || ymap === null) return;
    ymap.setCenter(next);
  },
);
watch(
  () => props.autocenterKey,
  () => {
    nextTick(autoCenterMap);
  },
);
watch(clusterMode, (next) => {
  if (shopsObjectManager === null) return;
  setTimeout(() => {
    // @ts-expect-error -- неполный тип
    shopsObjectManager.options.set("clusterize", next);
  }, MAP_RENDER_TICK);
});
</script>

<template>
  <section class="shop-select__map__outer">
    <div ref="mapEl" class="shop-select__map">
      <div v-if="loading" class="shop-select__map__loading">
        <VLoader size="m" color="project" />
      </div>
      <ShopSelectError v-if="loadingError" :desc="loadingErrorMessage" />
      <div v-else-if="displayMapElement" class="shop-select__map__container">
        <ShopSelectToolbar
          :buttons="toolbarButtons"
          @click:button="handleToolbarButtonClick"
        />
        <div :id="mapElementId" class="shop-select__map__ymap"></div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="postcss">
.shop-select__map {
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  position: absolute;
  inset: 0;
  z-index: 5;

  &__loading {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #ffffff40;
    z-index: 29;
  }

  &__outer {
    position: absolute;
    inset: 0;
    background: var(--pl-surface-primary-default);
    z-index: 10;
  }

  &__container,
  &__ymap {
    width: 100%;
    height: 100%;
  }
}
</style>
